import {
  cacheKeys,
  RoutineConfig,
  RoutineType,
  useUpdateMutation,
  useWorkspaceRoutine,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { RoutineEditForm } from './routineEditForm';

type EditRoutineProps = {
  routineId?: string;
};

export const EditRoutine = ({
  routineId: routineIdProp,
}: EditRoutineProps = {}) => {
  const { t } = useTranslation();
  const params = useParams();
  const routineId = routineIdProp ?? params.routineId;
  const queryClient = useQueryClient();
  const workspaceRoutine = useWorkspaceRoutine(routineId as string);
  const { mutateAsync: updateRoutine } = useUpdateMutation({
    modelName: 'WorkspaceRoutine',
    query: UPDATE_WORKSPACE_ROUTINE,
  });

  const onSubmit = async (
    data: RoutineConfig & { isWorkspaceRoutine?: boolean }
  ) => {
    const sortDataByQuestions = data.pages?.map((page) => ({
      ...page,
      questions: page.questions.map((q, index) => ({ ...q, order: index + 1 })),
    }));
    const { isWorkspaceRoutine, ...configData } = data;
    const input = {
      config: { ...configData, pages: sortDataByQuestions },
      name: data.title,
      id: routineId,
      isWorkspaceRoutine: isWorkspaceRoutine ?? false,
    };

    await updateRoutine({ input });

    await queryClient.invalidateQueries({
      queryKey: cacheKeys('routine').all,
    });
  };

  return (
    workspaceRoutine && (
      <RoutineEditForm
        config={workspaceRoutine?.config as RoutineConfig}
        type={workspaceRoutine?.type as RoutineType}
        onSubmit={onSubmit}
        saveButtonText={t('routines.admin.update')}
        workspaceId={workspaceRoutine?.workspace?.id}
        isWorkspaceRoutine={workspaceRoutine?.isWorkspaceRoutine}
      />
    )
  );
};

const UPDATE_WORKSPACE_ROUTINE = `
  mutation UpdateWorkspaceRoutine($input: MutateWorkspaceRoutineInput!) {
    updateWorkspaceRoutine(input: $input) {
      id
    }
  }
`;
